import { ThematicPracticesCarousel } from "@/components/ThematicPracticesCarousel";
import { MoodCardsSlider } from "@/components/MoodCardsSlider";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PortableText } from "@portabletext/react";
import { sanityClient } from "@/lib/sanity";
import { useLocale, pickLocalized, pickLocalizedRaw, tr, t, DEFAULT_LOCALE } from "@/lib/i18n";
import { portableComponents } from "@/lib/portable-text";
import { pageMeta, ogImageUrl, articleLd, JsonLd, excerptFromBlocks } from "@/lib/seo";

const QUERY = `*[_type == "learnMorePage" && (slug.en.current == $slug || slug.fr.current == $slug)][0]{
  _id, _updatedAt, eyebrow, title, content, imageCredits, slug, seo
}`;

const DESCRIPTION = "Explore how Matchamind works and the science behind it.";

/** Authored SEO description, else an excerpt of the English content, else the generic default. */
function metaDescription(page: any): string {
  return (
    pickLocalized(page?.seo?.metaDescription, DEFAULT_LOCALE) ||
    excerptFromBlocks(pickLocalizedRaw<any[]>(page?.content, DEFAULT_LOCALE)) ||
    DESCRIPTION
  );
}

export const Route = createFileRoute("/learn/$slug")({
  component: LearnMorePage,
  loader: async ({ params }) => {
    const page = await sanityClient.fetch<any>(QUERY, { slug: params.slug });
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData: page, params }) =>
    pageMeta({
      title: page
        ? pickLocalized(page.seo?.metaTitle, DEFAULT_LOCALE) ||
          `${pickLocalized(page.title, DEFAULT_LOCALE)} — Matchamind`
        : "Learn More — Matchamind",
      description: metaDescription(page),
      // Canonicalize to the English slug so /learn/<fr-slug> doesn't self-canonical into a duplicate.
      path: `/learn/${page?.slug?.en?.current ?? params.slug}`,
      ogImage: ogImageUrl(page?.seo?.ogImage),
      ogType: "article",
    }),
});

import { urlFor } from "@/lib/sanity";
import React from "react";

const HIGHLIGHT_WORDS = ["right", "bonne"];

function highlightWords(node: React.ReactNode): React.ReactNode {
  if (typeof node === "string") {
    const parts = node.split(new RegExp(`(\\b(?:${HIGHLIGHT_WORDS.join("|")})\\b)`, "gi"));
    return parts.map((part, i) =>
      HIGHLIGHT_WORDS.some((w) => w.toLowerCase() === part.toLowerCase()) ? (
        <span
          key={i}
          className="inline-block translate-y-1 rounded-[50px] bg-highlight-500 px-2 py-px font-display font-semibold italic text-[var(--background-main)]"
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  }
  if (Array.isArray(node)) return node.map((n, i) => <React.Fragment key={i}>{highlightWords(n)}</React.Fragment>);
  return node;
}

const learnPortableComponents = {
  ...portableComponents,
  block: {
    ...(portableComponents as any).block,
    h2: ({ children }: any) => (
      <h2 className="mt-12 mb-4 font-display text-[24px] font-semibold leading-[32px] tracking-[-0.072px] text-text-primary">
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="mt-8 mb-3 font-display text-[20px] font-semibold leading-[28px] tracking-[-0.04px] text-text-primary">
        {children}
      </h3>
    ),
    h1: ({ children }: any) => (
      <h1 className="mt-10 mb-10 font-display text-[28px] font-semibold leading-[36px] tracking-[0.2px] text-text-primary md:text-[34px] md:leading-[44px]">
        {highlightWords(children)}
      </h1>
    ),
    normal: ({ children }: any) => (
      <p className="my-2 font-sans text-[16px] font-normal leading-[24px] text-text-secondary">
        {children}
      </p>
    ),
    blockquote: ({ children }: any) => (
      <blockquote className="my-6 border-l-2 border-primary-200 pl-4 font-sans text-[16px] leading-[24px] italic text-text-secondary">
        {children}
      </blockquote>
    ),
  },
  list: {
    number: ({ children }: any) => (
      <ol className="my-4 list-decimal space-y-2 pl-8 font-sans text-[16px] font-light leading-[24px] text-text-secondary marker:font-semibold marker:text-text-primary">
        {children}
      </ol>
    ),
    bullet: ({ children }: any) => (
      <ul className="my-4 list-disc space-y-2 pl-6 font-sans text-[16px] font-light leading-[24px] text-text-secondary">
        {children}
      </ul>
    ),
  },
  marks: {
    ...(portableComponents as any).marks,
    highlight: ({ children }: any) => (
      <span className="inline-block translate-y-1 rounded-[50px] bg-highlight-500 px-2 py-px font-display font-semibold italic text-[var(--background-main)]">
        {children}
      </span>
    ),
  },
  types: {
    ...(portableComponents as any).types,
    image: ({ value }: any) => {
      if (!value?.asset) return null;
      return (
        <figure className="my-10 flex w-full flex-col items-center">
          <img
            src={urlFor(value).width(1200).url()}
            alt={value?.alt ?? ""}
            className="h-auto w-full object-cover"
          />
          {value?.caption && (
            <figcaption className="mt-2 text-xs text-text-muted">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
};

function LearnMorePage() {
  const { slug } = Route.useParams();
  const locale = useLocale();
  const search = locale === "en" ? {} : { lang: "fr" as const };
  const page = Route.useLoaderData();

  const eyebrow = pickLocalized(page.eyebrow, locale);
  const title = pickLocalized(page.title, locale);
  const body = pickLocalizedRaw<any[]>(page.content, locale);
  const credits = pickLocalized(page.imageCredits, locale);

  return (
    <article className="mx-auto flex w-full max-w-[1040px] flex-col items-start gap-6 px-6 pt-16 pb-8 md:px-10 md:pt-[100px] md:pb-[50px] lg:px-[80px]">
      <JsonLd
        data={articleLd({
          title: page.title,
          description: metaDescription(page),
          path: `/learn/${page.slug?.en?.current ?? slug}`,
          dateModified: page._updatedAt,
        })}
      />
      <Link
        to="/"
        search={search}
        className="text-sm text-text-muted hover:text-text-primary"
      >
        ← {tr(t.nav.home, locale)}
      </Link>
      {eyebrow && (
        <span className="rounded-full bg-secondary-300 px-2 py-1 font-sans text-[14px] font-medium uppercase leading-[14px] tracking-[0.1em] text-text-muted">
          {eyebrow}
        </span>
      )}
      <div className="w-full">
        {body && (
          <PortableText value={body} components={learnPortableComponents as any} />
        )}
        {(() => {
          const HIDE_SLIDER_SLUGS = new Set([
            "what-are-affirmations-and-how-do-they-work",
            "pratique-quotidienne-affirmations-personnalisees",
            "daily-practice-personalized-affirmations",
            "quest-ce-quune-affirmation-et-comment-ca-fonctionne",
          ]);
          const THEMED_SLUGS = new Set([
            "themed-practices-for-any-life-challenges",
            "themed-practices-for-life-challenges",
            "pratiques-thematiques-pour-les-defis-de-la-vie",
          ]);
          const enSlug = page.slug?.en?.current;
          const frSlug = page.slug?.fr?.current;
          const hide =
            HIDE_SLIDER_SLUGS.has(slug) ||
            (enSlug && HIDE_SLIDER_SLUGS.has(enSlug)) ||
            (frSlug && HIDE_SLIDER_SLUGS.has(frSlug));
          if (hide) return null;
          const isThemed =
            THEMED_SLUGS.has(slug) ||
            (enSlug && THEMED_SLUGS.has(enSlug)) ||
            (frSlug && THEMED_SLUGS.has(frSlug));
          return (
            <div className="w-full py-[70px]">
              {isThemed ? <ThematicPracticesCarousel layout="row" /> : <MoodCardsSlider />}
            </div>
          );
        })()}
        {credits && (
          <p className="mt-8 text-xs text-text-muted">{credits}</p>
        )}
      </div>
    </article>
  );
}